import { Component, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, debounceTime, distinctUntilChanged, EMPTY, merge, take, Observable } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { EmployeeRoleManager } from '../../../managers/employee-role.manager';
import { User } from '../../../models/ui/user.model';
import { Role } from '../../../models/ui/role.model';
import { Department } from '../../../models/ui/department.model';
import { BaseComponent } from '../../base.component';
import { NotificationManager } from '../../../managers/notification.manager';
import { isEqual, sortBy } from 'lodash-es';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatSelectionListChange } from '@angular/material/list';

@Component({
    selector: 'lex-employee-role',
    templateUrl: './employee-role.component.html',
    styleUrls: ['./employee-role.component.scss'],
    standalone: false
})
export class EmployeeRoleComponent extends BaseComponent {

	@ViewChild('leftScroll') leftScroll: CdkVirtualScrollViewport;

	public employees$: Observable<User[]>;
	public selectedUser: User = null;
	public employeeRoles: Role[];
	public departments: Department[];
	public selectedRoles: string[] = [];
	public selectedDepartment: string;

	private searchTerm = new BehaviorSubject<string>('');
	private searchText: string = '';

	public searchDepartment: string = '';

	public employeeRolesForm: UntypedFormGroup;

	constructor(
		private employeeRoleManager: EmployeeRoleManager,
		private notificationManager: NotificationManager) {
		super();
		this.employees$ = this.employeeRoleManager.employeeCollection$;
		merge(
			this.employeeRoleManager.getDepartments()
				.pipe(tap((departments) => this.departments = departments)),
			this.employeeRoleManager.getEmployeeRoles()
				.pipe(tap((employeeRoles) => this.employeeRoles = employeeRoles)),
		).subscribe();
		this.employeeRoleManager.refreshLexbeEmployees();
		this.searchTerm.pipe(
			debounceTime(500),
			distinctUntilChanged(),
			switchMap((term: string) => {
				this.searchText = term;
				if (term)
					this.selectedUser = null;
				else if (this.selectedUser)
					setTimeout(() => this.employees$.pipe(take(1)).subscribe(data => {
						this.leftScroll.scrollToIndex(data.findIndex(x => x.id === this.selectedUser.id));
					}));
				return EMPTY;
			}),
			takeUntil(this.destroy$)
		).subscribe();
		this.employees$.pipe(
			switchMap((data) => {
				if (this.selectedUser?.id)
					this.selectUser(data.find(x => x.id === this.selectedUser.id));
				return EMPTY;
			}),
			takeUntil(this.destroy$)
		).subscribe();
	}

	public selectUser(user: User): void {
		this.selectedUser = user;
		this.resetForm();
	}

	public filterEmployee(employees: User[]): User[] {
		if (this.searchText || this.searchDepartment) {

			if (this.searchText && this.searchDepartment)
				return employees.filter(x =>
					x.userName.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1 &&
					this.matchRolesToDepartmentName(x.roles) == this.searchDepartment);
			else if (this.searchText)
				return employees.filter(x =>
					x.userName.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1);
			else
				return employees.filter(x =>
					this.matchRolesToDepartmentName(x.roles) == this.searchDepartment);
		}
		return employees;
	}

	public onUpdateSearch(term: string): void {
		this.searchTerm.next(term);
	}

	public onUpdateDepartment(department?: string): void {
		if (department) {
			this.searchDepartment = department;
			this.selectedUser = null;
		}
		else {
			this.searchDepartment = '';
			if (this.selectedUser)
				setTimeout(() => this.employees$.pipe(take(1)).subscribe(data => {
					this.leftScroll.scrollToIndex(data.findIndex(x => x.id === this.selectedUser.id));
				}));
		}
	}

	public groupRoles(roles: Role[], start: number, end: number): Role[] {
		return roles.filter(y => y.rank > start && y.rank < end);
	}

	public matchRolesToDepartmentName(roles: Role[]): string {
		const department = this.matchRolesToDepartment(roles.map(x => x.name));
		if (department)
			return department.name;
		return '';
	}

	private matchRolesToDepartment(roles: string[]): Department {
		let department = this.departments.find(data =>
			isEqual(sortBy(data.roles.map(role => role.name)), sortBy(roles))
		);
		if (!department)
			department = this.departments.find(x => x.name === 'Custom')
		return department;
	}

	public changeDepartment(name: string): void {
		const department = this.departments.find(x => x.name === name);
		this.selectedDepartment = department.name;
		this.selectedRoles = department.roles.map(x => x.name);
		this.employeeRolesForm.get('userRoles').setValue(this.selectedRoles);
		this.employeeRolesForm.get('departments').setValue(this.selectedDepartment);
	}

	public changeUserRoles(event: MatSelectionListChange): void {
		event.options.forEach((option) => {
			const selectedValue = option.value;
			const index = this.selectedRoles.indexOf(selectedValue);
			if (index > -1)
				this.selectedRoles.splice(index, 1);
			else
				this.selectedRoles.push(selectedValue)
		})
		this.selectedDepartment = this.matchRolesToDepartment(this.selectedRoles).name;
		this.employeeRolesForm.get('departments').setValue(this.selectedDepartment);
	}

	public onCancel(): void {
		this.resetForm();
	}

	public onSubmit(): void {
		this.employeeRoleManager.updateUserEmployeeRoles({ userId: this.selectedUser.id, roles: this.selectedRoles })
			.subscribe(() => {
				this.employeeRoleManager.refreshLexbeEmployees();
				this.notificationManager.displaySnackBarMessage(
					{
						message: `User's employee roles updated successfully.`
					});
			},
				err => {
					this.notificationManager.displaySnackBarMessage({
						message: `Failed to update user's employee roles- ${err}`,
						type: 'error'
					});
					this.resetForm();
				});
	}

	private resetForm(): void {
		this.selectedRoles = this.selectedUser.roles.map(x => x.name);
		this.selectedDepartment = this.matchRolesToDepartmentName(this.selectedUser.roles);
		this.employeeRolesForm = new UntypedFormGroup({
			userRoles: new UntypedFormControl(this.selectedRoles),
			departments: new UntypedFormControl(this.selectedDepartment)
		});
	}
}
